import { useState, useEffect, useCallback } from "react";
import {
  useActionData,
  useNavigation,
  useSubmit,
  type ActionFunctionArgs,
} from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { parseAndFormatCsv, type DomainImportRow } from "@/lib/csv-utils";
import { getUnknownErrorMessage } from "@/lib/error-message";
import { FileUploader } from "@/components/import/FileUploader";
import { ImportPreview } from "@/components/import/ImportPreview";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle, AlertCircle } from "lucide-react";
import type { TablesInsert } from "@/types/database";
import {
  isImportDomainsSuccess,
  type ImportDomainsActionResult,
} from "@/types/import";

export async function importDomainsAction({
  request,
}: ActionFunctionArgs): Promise<ImportDomainsActionResult> {
  const formData = await request.formData();
  const file = formData.get("csvFile") as File;

  if (!file) return { error: "File not found" };

  try {
    const rawRows = await parseAndFormatCsv(file);

    if (rawRows.length === 0) return { error: "File is empty or invalid" };

    // Deduplicates by domain, last row wins, silently. Duplicates in the file
    // are dropped without any report to the user.
    const uniqueDomainsMap = new Map<string, DomainImportRow>();
    for (const row of rawRows) {
      uniqueDomainsMap.set(row.domain, row);
    }
    const rowsToInsert = Array.from(
      uniqueDomainsMap.values(),
    ) as TablesInsert<"domains">[];

    console.log(
      `After removing duplicates: ${rowsToInsert.length} unique domains out of ${rawRows.length}`,
    );

    // Batches are not a transaction: if batch N fails, batches 1..N-1 are
    // already committed and there is no rollback. The upsert also overwrites
    // every column present in the payload, including the ones cleanCsvValue
    // turned into null because the cell was empty.
    const BATCH_SIZE = 500;
    let totalInserted = 0;

    for (let i = 0; i < rowsToInsert.length; i += BATCH_SIZE) {
      const batch = rowsToInsert.slice(i, i + BATCH_SIZE);

      const { error } = await supabase
        .from("domains")
        .upsert(batch, { onConflict: "domain" });

      if (error) {
        throw new Error(
          `Error inserting batch ${i} to ${i + BATCH_SIZE}: ${error.message}`,
        );
      }

      totalInserted += batch.length;
      console.log(
        `Imported: ${totalInserted} / ${rowsToInsert.length} domains...`,
      );
    }

    return { success: true, count: totalInserted };
  } catch (err: unknown) {
    console.error("Import Error:", err);
    return { error: getUnknownErrorMessage(err) };
  }
}

export default function ImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<DomainImportRow[]>([]);

  const submit = useSubmit();
  const actionData = useActionData() as ImportDomainsActionResult | undefined;
  const navigation = useNavigation();

  const isUploading = navigation.state === "submitting";

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    setFile(selectedFile);

    try {
      const data = await parseAndFormatCsv(selectedFile);
      setPreviewData(data.slice(0, 5));
    } catch (error) {
      console.error("Preview error:", error);
    }
  }, []);

  const handleUpload = useCallback(() => {
    if (!file) return;
    const formData = new FormData();
    formData.append("csvFile", file);

    submit(formData, { method: "post", encType: "multipart/form-data" });
  }, [file, submit]);

  const handleCancel = useCallback(() => {
    setFile(null);
    setPreviewData([]);
  }, []);

  useEffect(() => {
    if (actionData && isImportDomainsSuccess(actionData)) {
      setFile(null);
      setPreviewData([]);
    }
  }, [actionData]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          Import CSV
        </h1>
        <p className="mt-2 text-muted-foreground">
          Upload your domain report to update the database!
        </p>
      </div>

      {actionData && (
        <Alert
          variant={
            isImportDomainsSuccess(actionData) ? "default" : "destructive"
          }
          className={
            isImportDomainsSuccess(actionData)
              ? "signal-card-positive border-[var(--signal-positive-border)] signal-text-positive"
              : ""
          }
        >
          {isImportDomainsSuccess(actionData) ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertTitle>
            {isImportDomainsSuccess(actionData) ? "Success" : "Error"}
          </AlertTitle>
          <AlertDescription>
            {isImportDomainsSuccess(actionData)
              ? `Successfully processed ${actionData.count} domains!`
              : actionData.error}
          </AlertDescription>
        </Alert>
      )}

      {!file && <FileUploader onFileSelect={handleFileSelect} />}

      {file && (
        <ImportPreview
          data={previewData}
          onUpload={handleUpload}
          onCancel={handleCancel}
          isUploading={isUploading}
        />
      )}
    </div>
  );
}
